import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import AdminLogin from "../components/admin/AdminLogin";
import MessageCard from "../components/admin/MessageCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
http.interceptors.response.use(undefined, async (error) => {
  const original = error.config;
  if (error.response?.status === 401 && !original._retried && !original.url.startsWith("/auth/")) {
    original._retried = true;
    refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
    await refreshing;
    return http(original);
  }
  return Promise.reject(error);
});

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/messages");
      setMessages(data.items);
    } catch {
      setAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    http.get("/auth/me")
      .catch(() => http.post("/auth/refresh").then(() => http.get("/auth/me")))
      .then(({ data }) => setAdmin(data))
      .catch(() => setAdmin(false));
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const logout = async () => {
    await http.post("/auth/logout");
    setAdmin(false);
    setMessages([]);
  };

  const fail = (err) => {
    if (err.response?.status === 401) setAdmin(false);
    toast.error(err.response?.status === 401 ? "Session expired. Please sign in again." : "Couldn't save that change. Please try again.");
  };

  const setStatus = async (id, status) => {
    try {
      await http.patch(`/admin/messages/${id}`, { status });
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (err) {
      fail(err);
    }
  };

  const remove = async (id) => {
    try {
      await http.delete(`/admin/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Message deleted");
    } catch (err) {
      fail(err);
    }
  };

  if (admin === null) return <main className="min-h-screen bg-[var(--ec-cream)]" data-testid="admin-loading" />;
  if (admin === false) return <AdminLogin http={http} onLogin={setAdmin} />;

  return (
    <main className="min-h-screen bg-[var(--ec-cream)] pb-20" data-testid="admin-inbox-page">
      <div className="h-20 bg-[var(--ec-navy)]" />
      <div className="max-w-4xl mx-auto px-5 lg:px-10 pt-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)]">Messages</h1>
            <p className="mt-2 text-sm text-[var(--ec-charcoal)]/75" data-testid="admin-message-count">
              {messages.length} message{messages.length === 1 ? "" : "s"} · signed in as {admin.email}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} disabled={loading} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 border border-[var(--ec-navy)] text-[var(--ec-navy)] hover:bg-[var(--ec-navy)] hover:text-white font-semibold px-5 py-2.5 rounded-full transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)] font-semibold px-5 py-2.5 rounded-full transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
        <ul className="mt-10 space-y-4" data-testid="admin-message-list">
          {messages.map((m) => (
            <MessageCard key={m.id} message={m} onStatus={setStatus} onDelete={remove} />
          ))}
        </ul>
        {!loading && messages.length === 0 && (
          <p className="mt-10 text-[var(--ec-charcoal)]/75" data-testid="admin-empty-state">No messages yet.</p>
        )}
      </div>
    </main>
  );
}
